import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { t } from '@aquilixshop/i18n';
import { useT } from '../../renderer/TextsProvider.js';
import { DEFAULT_COD_CONFIG, buyerEmailOf, dialCodeFor, flagEmojiFor, moroccanPhoneRuleApplies, phoneCountryOf, phoneNationalExampleFor, phonePlaceholderFor, resolveThemeVars, } from '@aquilixshop/cod-config';
import { marginFields, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, visibilityCss } from '../_shared/blockRender.js';
import { useCheckoutState, useProductState } from '../_shared/productContext.js';
import { hasOfferTiles, OfferTiles } from '../_shared/OfferTiles.js';
import { OFFER_TILE_VARS } from '../_shared/offerTileVars.js';
import { CardReveal, orderCtaStyle, resolveOrderAction } from '../_shared/orderAction.js';
import { CTA_FIT_CONTAINER, oneLineCta } from '../_shared/oneLineCta.js';
import { ltrIsolate, stripLtrIsolate } from '../_shared/ltrIsolate.js';
import { offerableMethods, resolveSelected, } from '../_shared/paymentMethods.js';
import { announceCheckoutIntent, announceContact, CodCartBeacon } from '../../lib/codCartBeacon.js';
import { clearPromoCode, readPromoCode } from '../../lib/promoLink.js';
import { formatOfferMoney, tierUnitPrice } from '../quantity/offerPricing.js';
import { basketLines } from '../_shared/productContext.js';
import { devStoreParam } from '../../sections/_shared/paymentClient.js';
import { builderCfOverrides, CF_VARIANTS, codDesignFields, codFormCss } from './design.js';
import { PostPurchaseUpsellChain } from './PostPurchaseUpsellChain.js';
import { CartUpsellChain, prefetchCartChain } from './CartUpsellChain.js';
import { UpsellOfferCard } from './UpsellOfferCard.js';
import { acceptedTotalCents, fetchUpsellChain, seedDeadline, upsellSubmission, withAcceptance, withoutAcceptance, } from './upsell.js';
/**
 * `cod-form` — the SELF-CONTAINED inline cash-on-delivery order block for the block-based `main-product`
 * buy-box (feature 0026). It is the Releasit / EasySell single-page COD funnel: the buyer fills the fields
 * AND orders IN ONE BLOCK, because directly under the fields it renders the order action — the COD/Card
 * selector (only once the store reports cards are enabled), the order submit button, and (on a CARD order)
 * the revealed Stripe Payment Element.
 *
 * SINGLE SOURCE OF TRUTH: the fields, their order / labels / placeholders / types / required flags, the
 * order-button label, the visual theme (`--cf-*`), and the text direction all come from the seller's
 * {@link CodConfig} — injected page-level via `context.codConfig` (the storefront fills it from the product
 * bundle, the builder loads it store-level). When absent (offline/older docs) it falls back to
 * {@link DEFAULT_COD_CONFIG}, so the form never renders empty. The dashboard Form designer and this storefront
 * render are now driven by the ONE config — "what the seller configures = what the customer sees".
 *
 * Reuse, not duplication: the fields bind to the SHARED checkout state ({@link useCheckoutState}); the button
 * calls the SAME {@link CheckoutState.placeOrder} the sibling `buy-buttons` block calls. The card reveal is
 * the shared {@link CardReveal} primitive.
 *
 * Outside a {@link CheckoutProductStateProvider} `useCheckoutState` is null → the block renders nothing.
 * SSR-safe: the form/selector render identically on server + first client paint because both sides receive the
 * SAME `context.codConfig` (SSR from the bundle, client rebuilt from the serialized island props).
 *
 * DESIGN lives in the builder (feature 0026-05): the labels / inputs / button / card are styled by the block's
 * OWN inspector settings — the full design catalogue in {@link ./design}. The block renders from `--cf-*`
 * custom properties via a scoped stylesheet ({@link codFormCss}); {@link builderCfOverrides} layers the
 * seller's design settings over the dashboard theme, so config feeds WHAT renders and block settings feed HOW
 * it looks. The ONE structural display knob kept as plain copy is the section heading (`formTitle`).
 */
/** Default section heading above the delivery fields — the validated Claude-Design "Informations de livraison". */
const FORM_TITLE = 'Informations de livraison';
export const codFormBlockSchema = {
    type: 'cod-form',
    name: 'Buy box',
    max: 1,
    // C'est ce bloc qui encaisse : le composeur IA pose ses briques juste AVANT lui.
    checkout: true,
    fields: [
        {
            // The ONE question this block asks the seller (feature 0030 — the payment-config simplification): what
            // is the buying JOURNEY? The payment METHOD is not asked here at all — it is what the store accepts
            // (Réglages → Paiements), and the buy-box offers whatever that is. The two used to be separate settings
            // that could contradict each other, which is how "Méthode de paiement = COD" silently lost to a store
            // with Stripe connected.
            //
            // 'auto' (default, and the stored value of every existing page) resolves from what the store accepts —
            // a card store gets the catalog flow, a COD-only store gets the on-page funnel. The explicit values
            // override it FOR THIS buy-box.
            id: 'orderAction',
            type: 'select',
            label: 'Buying journey',
            default: 'auto',
            options: [
                { value: 'auto', label: 'Automatic (from the accepted payments)' },
                { value: 'cod', label: 'On-page order form' },
                { value: 'add-to-cart', label: 'Add to cart' },
                { value: 'buy-now', label: 'Buy now (→ checkout)' },
            ],
        },
        {
            // How the form is PRESENTED (feature 0030 · step 4). This is appearance, so it belongs next to the rest
            // of the buy-box design — it used to live in the COD app's designer, which owns WHAT is collected, not
            // HOW it looks (the block's own stated split). Moving it here also makes it per-page: a landing page can
            // run the popup while the main product page stays inline.
            //
            // No schema default on purpose: an absent value falls back to the app's stored `layout`, so a seller who
            // had picked "Popup" there keeps it until they choose here. The builder displays that resolved value.
            id: 'layout',
            type: 'select',
            label: 'Form display',
            options: [
                { value: 'embedded', label: 'Embedded (always visible)' },
                { value: 'popup', label: 'Popup (button → window)' },
            ],
        },
        {
            // Promo-code field (feature 0014) — OPT-IN, off by default. Not every store runs codes, and an
            // always-on field teaches buyers to leave the funnel and go code-hunting; the seller who does run
            // codes turns it on per page, exactly like the layout above. The checkout page's own promo field
            // is unaffected (that surface is standard and always carries one).
            id: 'promoField',
            type: 'checkbox',
            label: 'Promo code field',
            default: false,
        },
        {
            id: 'formTitle',
            type: 'text',
            label: 'Form title',
            default: FORM_TITLE,
            maxLength: 80,
        },
        {
            // The order button's text, PER PAGE (founder, 2026-09-21: "chaque produit doit avoir son propre texte").
            // The COD app's `buttonLabel` stays the store-wide default and is what the builder shows greyed in the
            // box; a value typed here wins on THIS block only. Empty = inherit, so untouched pages keep following
            // the app.
            //
            // It is the text of the ORDER BUTTON on EVERY journey (founder, 24/09/2026: on her card store the
            // « Acheter maintenant » button had no way to say anything else). On the catalog journeys there is
            // no COD app text to inherit: empty = the journey's own wording in the shop language (« Acheter
            // maintenant » / « Ajouter au panier »), and on a card store's on-page funnel « Payer par carte ».
            id: 'buttonLabel',
            type: 'text',
            label: 'Button text',
            default: '',
            maxLength: 60,
            info: 'Empty = the text set in the Cash on delivery app. Each page can have its own.',
        },
        ...codDesignFields,
        visibilityField,
        ...marginFields(15, 15),
    ],
};
/**
 * Ask the storefront's same-origin discount-preview proxy (feature 0014) what a code is worth against the
 * current cart. Read-only server-side; the submit sends only the code and the backend re-prices. Buyer-facing
 * copy is FRENCH like the rest of the form. The refusal reasons are deliberately coarse (`invalid_code` /
 * `offer_conflict`) — the server never details WHY a code failed to a buyer.
 */
async function quotePromo(code, ctx) {
    if (!ctx.variantId)
        return { kind: 'error' };
    try {
        const res = await fetch('/api/discount-preview', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                body: {
                    lines: ctx.lines && ctx.lines.length > 0
                        ? ctx.lines
                        : [{ variantId: ctx.variantId, quantity: ctx.quantity }],
                    code,
                    paymentMethod: ctx.card ? 'CARD' : 'COD',
                    // The accepted upsell lines ride along so the preview prices the SAME cart the submit will —
                    // including the no-cumul offer/code conflict answer.
                    ...(ctx.upsell && ctx.upsell.accepted.length > 0 ? { upsell: ctx.upsell } : {}),
                },
                store: devStoreParam(),
            }),
        });
        if (res.status === 429) {
            return {
                kind: 'refused',
                messageKey: 'error.too_many_attempts',
            };
        }
        if (!res.ok)
            return { kind: 'error' };
        const parsed = (await res.json());
        if (parsed.kind !== 'ok' || !parsed.data)
            return { kind: 'error' };
        if (parsed.data.applicable && parsed.data.discount) {
            return {
                kind: 'ok',
                amountCents: parsed.data.discount.amount,
                currency: parsed.data.discount.currency,
            };
        }
        return {
            kind: 'refused',
            // A key rather than a sentence: `quotePromo` is a module function with no context, and a sentence
            // decided here could never follow the shop's language (feature 0050).
            messageKey: parsed.data.reason === 'offer_conflict' ? 'cod.promo_not_combinable' : 'cod.promo_invalid',
        };
    }
    catch {
        return { kind: 'error' };
    }
}
function renderField(field, blockId, value, countryCode, onChange) {
    const id = `cod-${blockId}-${field.id}`;
    // Phone placeholder is derived from the selling country (never a hardcoded "+212") when the seller left
    // it blank; the seller's own placeholder always wins.
    const ph = field.type === 'phone'
        ? field.placeholder || phonePlaceholderFor(countryCode) || field.label
        : field.placeholder || field.label;
    // The city field is a PLAIN free-text input — the buyer types their city, no list, no picker. The geo
    // autocomplete was removed: a curated/dr5hn list never covers every douar and forcing a pick cost orders.
    // Shared props valid on input / textarea / select (placeholder is added per-element, not on <select>). The
    // `cf-input` class is what the scoped stylesheet targets — scoping to it (not `.${cls} input`) keeps the
    // payment-selector radios out of the field styling.
    const base = {
        id,
        name: field.id,
        required: field.required,
        value,
        'aria-label': field.label,
        className: 'cf-input',
        onChange: (ev) => onChange(ev.target.value),
    };
    // The `textarea` (delivery note) AND the `address` field are multi-line — the validated design renders
    // "Adresse complète" as a 2-row textarea ("N°, rue, quartier, repère…"), matching the note field.
    if (field.type === 'textarea' || field.type === 'address') {
        return _jsx("textarea", { ...base, placeholder: ph, rows: 2 });
    }
    if (field.type === 'select') {
        return (_jsxs("select", { ...base, children: [_jsx("option", { value: "", disabled: true, children: ph }), (field.options ?? []).map((o) => (_jsx("option", { value: o, children: o }, o)))] }));
    }
    const type = field.type === 'phone'
        ? 'tel'
        : field.type === 'email'
            ? 'email'
            : field.type === 'number'
                ? 'number'
                : 'text';
    return (_jsx("input", { ...base, placeholder: ph, type: type, inputMode: field.type === 'phone' ? 'tel' : undefined, autoComplete: field.type === 'city' ? 'address-level2' : undefined }));
}
/** Count of digits in a phone value — the shared basis for the valid-check + the "9 chiffres" error. */
function phoneDigits(value) {
    return value.replace(/\D/g, '');
}
/**
 * The shapes a Moroccan number may legitimately take in this field — a RANGE, not a floor.
 *
 * A one-sided `>= 9` check passed anything longer and still painted the green tick, so a double-prefixed entry
 * ("06" typed in front of an already-complete "0634840799") looked accepted, and the backend's normalizer
 * dutifully turned those 12 digits into `+21260634840799` and burned a real WhatsApp send on a number that
 * cannot exist. The three accepted forms mirror exactly what `E164PhoneNormalizer` can format for MA, so the
 * field and the sender never disagree about what is dialable:
 *   `634840799` (9, national) · `0634840799` (10, trunk 0) · `212634840799` / `+212…` (12, country code pasted).
 */
function isValidMoroccanPhone(value) {
    const d = phoneDigits(value);
    if (d.length === 9)
        return !d.startsWith('0');
    if (d.length === 10)
        return d.startsWith('0');
    if (d.length === 12)
        return d.startsWith('212');
    return false;
}
/**
 * Phone field (the validated design): a `+dial`(country-derived) prefix box + the number input + a green
 * valid-check that appears once the number looks complete. [isComplete] is injected by the parent (which alone
 * knows whether the Moroccan rule is switched on) so the tick and the blocking error can never disagree — a tick
 * over a number the submit gate rejects is worse than no tick at all. SSR-safe: the check is a pure function of
 * the value, and the focus ring is CSS `:focus-within` (no state), so server and first client paint match.
 */
function PhoneField({ id, name, value, placeholder, required, ariaLabel, dial, flag, isComplete, onChange, }) {
    const valid = isComplete(value);
    return (_jsxs("div", { className: "cf-phone", children: [dial ? (_jsxs("span", { className: "cf-phone-dial", children: [flag ? _jsx("span", { className: "cf-phone-flag", children: flag }) : null, _jsx("span", { className: "cf-phone-code", children: dial })] })) : null, _jsx("input", { id: id, name: name, className: "cf-input", type: "tel", inputMode: "tel", value: value, placeholder: placeholder, required: required, "aria-label": ariaLabel, autoComplete: "tel", onChange: (e) => onChange(e.target.value) }), valid ? (_jsx("span", { className: "cf-phone-ok", "aria-hidden": "true", children: _jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", children: [_jsx("circle", { cx: "8", cy: "8", r: "8", fill: "var(--cf-accent-tint,var(--cf-soft,#eef7f3))" }), _jsx("path", { d: "M4.8 8.2 L7 10.4 L11.2 5.8", fill: "none", stroke: "var(--cf-accent,#0e7a5f)", strokeWidth: "1.7", strokeLinecap: "round", strokeLinejoin: "round" })] }) })) : null] }));
}
/**
 * Popup layout (`cfg.layout === 'popup'`) — the buyer first sees only a trigger button; clicking it opens the
 * form in a centred modal (the Releasit "popup" form type). SSR-safe: `open` starts `false`, so the server and
 * the first client paint both render JUST the trigger (no mismatch).
 *
 * The overlay is PORTALLED to `document.body`: the builder device-frame sets `contain: paint`, which makes it
 * the containing block for a `position:fixed` child, so an in-place modal would be trapped + clipped inside the
 * frame (invisible when scrolled). Portalling out of the frame makes the builder behave like the storefront
 * (viewport-fixed + centred). To keep the seller's `--cf-*` tokens + the scoped `.cf-*` styling, the portal is
 * re-wrapped in a `.aqx-cf-<id>` context element (its own card box is neutralised by `.cf-pop-portal`).
 *
 * Events: the canvas hijacks CLICK for selection (capture-phase stopPropagation), so in the builder the controls
 * fire on MOUSEDOWN (which it doesn't eat); the storefront uses click. In the editor the scrim is transparent +
 * click-through so the panel floats over the canvas while the rail/inspector stay usable. Closes on ✕/scrim/Esc.
 */
function CodPopup({ triggerLabel, triggerFit, triggerStyle, dir, editor = false, wrapperClass, wrapperStyle, variant, labels, open, onOpenChange, onWarm, children, }) {
    const on = (fn) => (editor ? { onMouseDown: fn } : { onClick: fn });
    // Never in the editor: the seller pointing at her own button must not make the storefront ask the server
    // about a synthetic product.
    const warm = editor || !onWarm ? {} : { onPointerEnter: onWarm, onPointerDown: onWarm, onFocus: onWarm };
    return (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", style: triggerStyle, "data-aqx-buybox-trigger": "", ...warm, ...on(() => onOpenChange(!open)), children: _jsx("span", { style: triggerFit, children: triggerLabel }) }), _jsx(CodModal, { open: open, onClose: () => onOpenChange(false), dir: dir, editor: editor, wrapperClass: wrapperClass, wrapperStyle: wrapperStyle, variant: variant, labels: labels, children: children })] }));
}
/**
 * The centred modal itself, portalled to `document.body` — extracted from {@link CodPopup} because the
 * upsell chain (feature 0042 lot D) needs the SAME shell in the EMBEDDED layout, where there is no trigger
 * button and no popup form: there the chain plays on "Commander", just before the order goes out.
 *
 * Portalling is not cosmetic: the builder's device-frame sets `contain: paint`, which would make it the
 * containing block of a `position:fixed` child and trap the modal inside the frame.
 */
function CodModal({ open, onClose, dir, editor = false, wrapperClass, wrapperStyle, variant, labels, children, }) {
    // This shell takes no RenderContext — it is a local presentational wrapper. React context crosses
    // portals, so the provider mounted by PageRenderer reaches it here (feature 0050).
    const tr = useT();
    const on = (fn) => (editor ? { onMouseDown: fn } : { onClick: fn });
    useEffect(() => {
        if (!open)
            return;
        const onKey = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- onClose is re-created every render
    }, [open]);
    if (!open || typeof document === 'undefined')
        return null;
    return createPortal(_jsx("div", { className: `${wrapperClass} cf-pop-portal`, "data-cf-input": variant, "data-cf-labels": labels, style: wrapperStyle, dir: dir, children: _jsx("div", { className: "cf-pop-ov", "data-editor": editor || undefined, ...on(onClose), children: _jsxs("div", { className: "cf-pop-panel", role: "dialog", "aria-modal": "true", dir: dir, onClick: (e) => e.stopPropagation(), onMouseDown: (e) => e.stopPropagation(), children: [_jsx("button", { type: "button", className: "cf-pop-close", ...on(onClose), "aria-label": tr('a11y.close'), children: "\u00D7" }), children] }) }) }), document.body);
}
/**
 * CATALOG-mode order action (feature 0030). Renders the buy-box's primary CTA when the effective `orderAction`
 * is `'add-to-cart'` or `'buy-now'` (dropping the single-page COD/card funnel). Variant + quantity come from the
 * SHARED product state (the sibling variant-picker / quantity blocks write it), so this block renders ONLY the
 * CTA. The cart WRITE is host-injected (`context.addToCart`, mirrors `renderCardPayment`) so ui-react stays
 * cart-free; `'buy-now'` hands the lines to `context.buyNow` instead, which takes the buyer straight to the
 * checkout WITHOUT touching the cart (28/09/2026 — it used to write the cart first, and the product stayed
 * there). In the builder/demo both host slots are undefined → the CTA renders (so the seller sees the final
 * button) but neither writes nor navigates. Money is a display snapshot only — checkout re-prices from
 * `(variantId, quantity)`.
 */
function CartAddBox({ context, ps, btnStyle, action, pageLabel, }) {
    const [added, setAdded] = useState(false);
    const buyNow = action === 'buy-now';
    // ── Feature 0069 lot C — the pre-purchase chain, in the CART journey ────────────────────────────────
    // Prefetched on first INTENT (hover / focus on the button), never on mount: one request per product page
    // view would be load for a feature most shops do not run, and the COD form has always fetched on intent
    // too. By the time the click lands the chain is usually in hand, so the offer opens instead of a spinner.
    const [chain, setChain] = useState(null);
    const chainAsked = useRef(false);
    const [playing, setPlaying] = useState(false);
    /**
     * The in-flight prefetch, kept so the CLICK can wait for it.
     *
     * Intent and click are ~200ms apart for an ordinary hand, and a tap has no hover at all — far less than a
     * round trip. Reading `chain` from state at click time therefore read `null` on the normal gesture, and the
     * buyer got their product added with the seller's offer never shown (founder, 2026-09-22: « je test encore
     * et je vois rien » — measured: no window under 1s between hover and click).
     */
    const chainPromise = useRef(null);
    /**
     * The click landed before the chain did, and we are WAITING for the answer before deciding.
     *
     * Never an open window: a window that opens and closes because the product had no offer is a flash the
     * buyer reads as a glitch (founder, 23/09/2026 — she clicked a catalogue product whose only chain is
     * post-purchase and saw a small popup blink). The button carries the wait instead, and the window opens
     * ONLY when there is something to show.
     */
    const [awaiting, setAwaiting] = useState(false);
    const alive = useRef(true);
    useEffect(() => () => {
        alive.current = false;
    }, []);
    const prefetch = () => {
        if (chainAsked.current || context.renderMode !== 'live')
            return;
        chainAsked.current = true;
        chainPromise.current = prefetchCartChain(ps.product.id, devStoreParam()).then((c) => {
            if (alive.current)
                setChain(c);
            return c;
        });
    };
    // On a LIVE product page `ps.variantId` is the REAL variant UUID (resolveProduct → pickerVariants). Prefer the
    // priced real variant for the snapshot; fall back to the option-derived DemoVariant + base price (builder/demo).
    const realVariants = ps.product.variants ?? [];
    const variantId = ps.variantId || realVariants[0]?.id || '';
    // The basket as lines — a variant PER UNIT of the offer (28/09/2026): `[{M,1},{L,2}]` for a mixed pack,
    // the one historic line when nobody touched a unit row. Every unit must be purchasable for the button
    // to be: one sold-out colour in the pack is a sold-out pack.
    const lines = basketLines(ps);
    const available = lines.every((l) => {
        const rv = realVariants.find((v) => v.id === l.variantId);
        const dv = ps.variants.find((v) => v.id === l.variantId);
        return rv?.available ?? dv?.available ?? true;
    });
    // Feature 0069 lot B — the SELECTED quantity-offer tier, when this product has one. Same rule as the
    // backend engine (`QuantityOfferEngine.selectTier`): the HIGHEST tier whose pack size fits the chosen
    // quantity, so an off-tier quantity still resolves to the tier checkout charges. Without this the cart
    // snapshotted the LIST price while the server charged the tier, and the total dropped between the cart and
    // the payment page — "displayed == charged" broken on the very journey a card shop uses.
    const catalogOffer = context.activeOffer;
    const catalogTier = catalogOffer && catalogOffer.productId === ps.product.id
        ? catalogOffer.tiers.reduce((best, t) => (t.qty <= ps.quantity && (!best || t.qty > best.qty) ? t : best), undefined)
        : undefined;
    const onAdd = () => {
        if (!available || !variantId)
            return;
        const cover = ps.product.images[0];
        const compareAt = ps.product.compareAtPrice?.amount ?? null;
        // ONE cart line per distinct variant in the basket (a variant per unit, 28/09/2026) — the cart merges by
        // variant and the server prices the offer on the product's TOTAL, so three lines of one Medium and two
        // Large quote exactly as one line of three would. Each line is priced by the SAME function that draws
        // the tiles, against ITS variant's list price, so the number in the cart is the number the buyer just
        // tapped. The currency guard is the engine's: a FIXED rule expressed in another currency does not apply,
        // and the line stays at list price.
        const cartLines = lines.map((l) => {
            const rv = realVariants.find((v) => v.id === l.variantId);
            const dv = ps.variants.find((v) => v.id === l.variantId);
            const money = rv?.price ?? ps.product.price;
            const lineLabel = rv?.label ?? dv?.label ?? '';
            const tierUnit = catalogTier && catalogOffer
                ? tierUnitPrice(money.amount, catalogTier, catalogOffer.currency.toUpperCase() === money.currency.toUpperCase())
                : money.amount;
            return {
                variantId: l.variantId,
                productId: ps.product.id,
                productSlug: ps.product.slug,
                title: ps.product.name,
                variantLabel: lineLabel || null,
                unitPriceAmount: tierUnit,
                ...(catalogTier ? { tierId: catalogTier.tierId } : {}),
                // Capture the compare-at ("was") price so the cart shows the struck price + "you save" (only when
                // it's a real markdown above the unit price). Product-level compare-at (our model has no per-variant
                // compare-at). With a tier applied the reference to strike is this variant's LIST price — that is
                // the saving the buyer actually gets — and it wins over a product-level compare-at that sits lower.
                compareAtAmount: tierUnit < money.amount
                    ? Math.max(money.amount, compareAt ?? 0)
                    : compareAt && compareAt > money.amount
                        ? compareAt
                        : null,
                currency: money.currency,
                imageUrl: cover?.url ?? null,
                qty: l.quantity,
                // Feature 0078 lot B — so the checkout opens without an address block on a file.
                ...(ps.product.requiresShipping === false ? { requiresShipping: false } : {}),
            };
        });
        // Feature 0069 lot C — the offers the buyer took, as lines of their own, BEFORE the main product's.
        const acceptedLines = takenRef.current.map((a) => {
            const offer = chain?.offers.find((o) => o.offerId === a.offerId);
            const up = chain
                ? {
                    upsellId: chain.upsellId,
                    version: chain.version,
                    offerId: a.offerId,
                    triggerProductId: ps.product.id,
                    // The offer's cap travels WITH the line so the cart can offer a stepper it knows the server
                    // will honour (23/09/2026). Without it the cart would either freeze the quantity — which is
                    // what the buyer read as a broken row — or risk a 422 that refuses the whole cart.
                    ...(offer ? { maxQuantity: offer.maxQuantity } : {}),
                }
                : undefined;
            return {
                variantId: a.variantId,
                // The OFFERED product, not the one on the page — an upsell sells something else, and the cart
                // needs its identity to quote the line and to link back to it.
                productId: offer?.product.productId ?? '',
                productSlug: ps.product.slug,
                title: a.title,
                variantLabel: a.variantLabel || null,
                // Display only, like every snapshot: the server re-derives this line FROM THE STORED OFFER.
                unitPriceAmount: a.unitCents,
                compareAtAmount: a.baseUnitCents > a.unitCents ? a.baseUnitCents : null,
                currency: a.currency,
                imageUrl: offer?.product.images[0]?.url ?? null,
                qty: a.quantity,
                ...(up ? { upsell: up } : {}),
            };
        });
        takenRef.current = [];
        if (buyNow) {
            // « Acheter maintenant » NEVER touches the cart (28/09/2026). It used to be « write the cart, then
            // navigate to /checkout » — and the founder found her « direct » purchase sitting in the cart: the
            // badge counted it, the drawer listed it, it merged with whatever was already in there, and it stayed
            // after an abandoned checkout. The host takes EXACTLY these lines straight to its checkout, the way
            // Shopify's « Buy it now » does. Undefined host slot (builder/demo) → the button stays inert.
            context.buyNow?.([...acceptedLines, ...cartLines]);
            return; // no "Ajouté ✓" feedback — we're leaving the page
        }
        // writeCart (via the host slot) persists synchronously to localStorage. Undefined in builder/demo → no-op.
        // The offers ride in FIRST, silently, and the main product's lines LAST: the drawer is opened by the very
        // last write, so it slides in once, already holding everything. Writing the main line first would open
        // it on a cart that is still missing what the buyer just accepted.
        for (const line of acceptedLines)
            context.addToCart?.(line, { openCart: false });
        cartLines.forEach((line, i) => {
            context.addToCart?.(line, { openCart: i === cartLines.length - 1 });
        });
        setAdded(true);
        window.setTimeout(() => setAdded(false), 2500);
    };
    /** What the buyer accepted in the chain, waiting to be written just before the main line. */
    const takenRef = useRef([]);
    /** Played already on this page view — see [onClick]. */
    const chainPlayed = useRef(false);
    /**
     * The click. With a chain in hand it plays FIRST and the cart is written when it ends — closing the window,
     * refusing every offer or a chain that never loaded all end in the same place, with the product added.
     *
     * ONCE per page view. Without [chainPlayed] a buyer who taps "add to cart" a second time — to buy two, or
     * because they were not sure the first tap registered — is shown the same offers they just declined. An
     * offer asked twice is not a second chance, it is nagging, and the second answer is the same one.
     */
    const onClick = () => {
        if (!available || !variantId || playing)
            return;
        // The chain is already in hand (the buyer hovered long enough, or came back to the button).
        if (chain && chain.offers.length > 0 && !chainPlayed.current) {
            chainPlayed.current = true;
            setPlaying(true);
            return;
        }
        if (chainPlayed.current) {
            onAdd();
            return;
        }
        // It is NOT in hand. A tap never hovers, and an ordinary hand clicks ~200ms after pointing — both beat
        // the round trip, so reading `chain` here used to answer null on the ORDINARY gesture and the seller's
        // offer was skipped (founder, 22/09/2026). Start the fetch if intent never fired, then open the window
        // on its skeleton straight away: the click has a visible answer at once, and `fetchUpsellChain` caps
        // itself (3s) and fails open, so the wait can neither hang nor cost the sale.
        prefetch();
        const inFlight = chainPromise.current;
        if (!inFlight) {
            // Builder / demo: no fetch is ever made here, so there is nothing to wait for.
            onAdd();
            return;
        }
        setAwaiting(true);
        void inFlight.then((c) => {
            if (!alive.current)
                return;
            setAwaiting(false);
            // Something to show ⇒ the window. Nothing ⇒ the product goes in the cart and the buyer never learns
            // an offer was even looked for. `fetchUpsellChain` caps itself (3s) and fails open, so this decision
            // always arrives and never costs the sale.
            if (c && c.offers.length > 0 && !chainPlayed.current) {
                chainPlayed.current = true;
                setPlaying(true);
                return;
            }
            chainPlayed.current = true;
            onAdd();
        });
    };
    const addBtnStyle = {
        ...btnStyle,
        // The wait is visible on the button, never as a window that opens on nothing.
        ...(awaiting ? { cursor: 'progress' } : {}),
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
    };
    const ctaLabel = !available
        ? t(context, 'product.sold_out')
        : buyNow
            ? pageLabel || t(context, 'product.buy_now')
            : added
                ? t(context, 'cart.added')
                : pageLabel || t(context, 'product.add_to_cart');
    return (_jsxs("div", { className: "cf-actions", style: { display: 'flex', flexDirection: 'column', gap: '12px' }, children: [_jsx("button", { type: "button", style: addBtnStyle, onClick: onClick, onPointerEnter: prefetch, onFocus: prefetch, disabled: !available, "aria-busy": awaiting, "aria-live": "polite", children: ctaLabel }), playing ? (_jsx(CartUpsellChain, { chain: chain, onFinished: (taken) => {
                    takenRef.current = taken;
                    setPlaying(false);
                    onAdd();
                } })) : null] }));
}
/**
 * The builder-only "COD app is off" explainer (feature 0026 app gate). Inline styles on purpose: catalog mode
 * skips the block's scoped stylesheet, and this notice must never leak into the live storefront's CSS.
 */
/** Feature 0078 lot B — a digital product the store cannot sell (no card): a plain sentence in place of a button. */
const DIGITAL_UNAVAILABLE_STYLE = {
    margin: 0,
    padding: '12px 14px',
    borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.14)',
    background: 'rgba(0,0,0,0.03)',
    fontSize: 14,
    lineHeight: 1.45,
};
/** Feature 0078 lot B — the one line that tells a buyer a file arrives by e-mail, above the button. */
const DIGITAL_NOTE_STYLE = {
    margin: '0 0 10px',
    fontSize: 13,
    lineHeight: 1.45,
    opacity: 0.8,
};
const COD_OFF_NOTICE_STYLE = {
    margin: '0 0 10px',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px dashed rgba(0,0,0,0.18)',
    background: 'rgba(0,0,0,0.03)',
    fontSize: 13,
    lineHeight: 1.45,
    opacity: 0.85,
};
/**
 * Fallback when the host declares no methods (older documents, test harnesses): COD, the method every store
 * had before card acceptance existed. An empty declaration is a real answer ("this store accepts nothing")
 * and is NOT defaulted — only an absent one is.
 */
const DEFAULT_METHODS = ['COD'];
/**
 * How the buy-box form is presented: the BLOCK setting when the seller has chosen one, else the COD app's
 * stored layout (feature 0030 · step 4 — the setting moved from the app to the block, and this is what makes
 * the move a no-op for every page that predates it). Exported so the builder's inspector can display the same
 * resolved value it would render, rather than a blank select.
 */
export function resolveLayout(blockLayout, appLayout) {
    return blockLayout === 'popup' || blockLayout === 'embedded' ? blockLayout : appLayout;
}
const CodFormBlock = ({ block, context }) => {
    const cs = useCheckoutState();
    // Shared product state (quantity / variant / product) — used for the summary pricing + the offer tiles' selected
    // tier. Non-null under a buy-box provider; called BEFORE the null-guard so hook order stays stable.
    const ps = useProductState();
    // `attempted` gates the per-field error display until the buyer tries to submit (the real guard stays in
    // `cs.placeOrder`). `reopen` un-dismisses the success card so "Modifier ma commande" edits the same order.
    // Both start false → SSR and first client paint are byte-identical.
    const [attempted, setAttempted] = useState(false);
    const [reopen, setReopen] = useState(false);
    // ── Pre-purchase 1-Click Upsell chain (feature 0042 lot D) ────────────────────────────────────────────
    // The state lives HERE, not in CodPopup, because the popup unmounts its body on every close: a chain owned
    // by the popup would re-offer what the buyer already accepted and restart its countdown each time it
    // reopened. Everything starts in the "nothing yet" state, so SSR and the first client paint are identical
    // and nothing is fetched until the buyer actually asks to order.
    const [popupOpen, setPopupOpen] = useState(false);
    // EMBEDDED layout only: the chain has no popup to live in, so it plays in the card's own place at the
    // moment the buyer taps "Commander" — the strongest point of intent on the page. `pendingSubmit` remembers
    // that an order is waiting behind it, so finishing the chain places it without a second click.
    const [chainGateOpen, setChainGateOpen] = useState(false);
    const [pendingSubmit, setPendingSubmit] = useState(false);
    // The order is placed from an EFFECT, not from the accept handler, and that is not incidental: accepting
    // the last offer calls setAccepted() and ends the chain in the same tick, so a submit fired inline would
    // read the PREVIOUS accepted list and place the order without the offer the buyer just took. The ref is
    // re-pointed at every render, so by the time this runs it closes over the settled state.
    const submitAfterChain = useRef(null);
    useEffect(() => {
        if (!pendingSubmit || chainGateOpen)
            return;
        setPendingSubmit(false);
        submitAfterChain.current?.();
    }, [pendingSubmit, chainGateOpen]);
    // The POST-purchase chain, played in this same shell once the order exists, before the thank-you page.
    const [postOrderId, setPostOrderId] = useState(null);
    /**
     * The post-purchase chain has something on screen — an offer, or the note after an answer.
     *
     * The shell used to be handed over the moment the order was placed, before anyone knew whether there was
     * an offer at all: an order with no chain to play showed an EMPTY popup for the length of a fetch and
     * then redirected (founder, 23/09/2026). The chain now announces itself, and until it does, the buyer
     * keeps looking at their confirmed order.
     */
    const [postChainOnScreen, setPostChainOnScreen] = useState(false);
    const proceedRef = useRef(null);
    const [chain, setChain] = useState(null);
    const [chainPhase, setChainPhase] = useState('idle');
    const [offerIndex, setOfferIndex] = useState(0);
    const [accepted, setAccepted] = useState([]);
    // offerId → epoch ms. Set the first time an offer is SHOWN and never reset: a countdown that restarts is
    // an urgency the shop cannot honour, and buyers notice.
    const [deadlines, setDeadlines] = useState({});
    // True once a submit has left the browser. From then on the accepted lines are FROZEN: the idempotency key
    // is only regenerated after a confirmed success, so a retry replays the ORIGINAL body — and the server
    // answers a replay with the order it already placed, without re-reading the lines. Letting the buyer edit
    // the list after that would show them a basket the order does not contain.
    const [submitSent, setSubmitSent] = useState(false);
    // ── promo code (feature 0014) ─────────────────────────────────────────────────────────────────────────
    // The APPLIED code lives in the shared checkout state (`cs.promo`) so `placeOrder` attaches it whichever
    // block submits; only the field's transient UI state lives here. Everything starts closed/empty → SSR and
    // the first client paint stay byte-identical.
    const [promoOpen, setPromoOpen] = useState(false);
    const [promoInput, setPromoInput] = useState('');
    const [promoBusy, setPromoBusy] = useState(false);
    const [promoError, setPromoError] = useState(null);
    // What the NEXT quote should price — assigned every render (same pattern as `submitAfterChain`) so the
    // re-quote effect below reads current values without listing half the render scope as dependencies.
    const promoQuoteCtx = useRef({ variantId: '', quantity: 1, card: false });
    // Keep an applied code honest: a quantity/variant/upsell change re-asks the preview what the code is
    // worth NOW. A refusal drops the code VISIBLY (field reopened, message shown) — never a silently stale
    // amount; a network failure keeps the last quote (placement re-validates authoritatively anyway).
    useEffect(() => {
        if (!cs?.promo)
            return;
        const applied = cs.promo;
        const setPromo = cs.setPromo;
        const timer = window.setTimeout(() => {
            void quotePromo(applied.code, promoQuoteCtx.current).then((res) => {
                if (res.kind === 'ok') {
                    setPromo({ code: applied.code, amountCents: res.amountCents, currency: res.currency });
                }
                else if (res.kind === 'refused') {
                    setPromo(null);
                    setPromoInput(applied.code);
                    setPromoOpen(true);
                    setPromoError(t(context, res.messageKey));
                }
            });
        }, 400);
        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- the quote inputs live in promoQuoteCtx (a ref); these deps are the re-quote TRIGGERS, not the values
    }, [cs?.promo?.code, ps?.quantity, ps?.variantId, ps?.unitVariantIds.join('|'), accepted]);
    /**
     * The PROMOTED code (09/10/2026) — one the shopper never typed, carried in the link she clicked.
     *
     * Without this the promotional link did nothing at all in the COD funnel, which is the funnel that
     * actually sells: the field above is seller opt-in and off by default, so on most pages there was no
     * field to prefill, and this page places the order itself — it never passes through /checkout where a
     * code could be caught later.
     *
     * Tried at most ONCE per code per mount, and only once the variant is resolved: quoting against an empty
     * variant asks the backend to price a cart that does not exist yet, and the refusal would then clear a
     * perfectly good code.
     */
    const promoLinkTried = useRef(null);
    useEffect(() => {
        if (!cs || cs.promo)
            return;
        const setPromo = cs.setPromo;
        const code = readPromoCode();
        if (!code || promoLinkTried.current === code)
            return;
        const quoteCtx = promoQuoteCtx.current;
        // Assigned during render, further down — empty until the buy box knows which variant it is selling.
        if (!quoteCtx.variantId)
            return;
        promoLinkTried.current = code;
        void quotePromo(code, quoteCtx).then((res) => {
            if (res.kind === 'ok') {
                setPromo({ code, amountCents: res.amountCents, currency: res.currency });
                return;
            }
            if (res.kind === 'refused') {
                // An expired campaign, a code for another shop, a cap reached. Forgotten rather than re-asked on
                // every page of the visit — and SILENTLY: she did not type this code, so a red message about it
                // would be an accusation about a link she followed, not a correction of something she did.
                clearPromoCode();
            }
            // `failed` (offline, rate-limited, a 500) keeps the code: the cart or the checkout will try again.
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- the quote inputs live in promoQuoteCtx (a ref); these deps are the TRIGGERS that make a quote possible
    }, [cs?.promo, ps?.variantId, ps?.quantity, ps?.unitVariantIds.join('|'), accepted]);
    /**
     * Feature 0072 étape 4 — qui est l'acheteuse, au moment où elle le tape (même règle que CheckoutForm).
     *
     * AU-DESSUS du `return null` ci-dessous, et ce n'est pas un détail de style : ce bloc vivait sous le
     * retour anticipé, donc ses deux hooks n'étaient appelés que quand `cs` existait. Basculer
     * `orderAction` sur un bloc déjà monté changeait le NOMBRE de hooks rendus et React levait
     * « Rendered fewer hooks than expected », ce qui casse l'îlot entier — le formulaire de commande,
     * c'est-à-dire la seule chose qui encaisse. Le garde-fou qui l'a attrapé existait déjà :
     * « re-renders from the COD form to the add-to-cart CTA without a hook-order error ».
     *
     * Conséquence : tout est relu DEPUIS `cs` à l'intérieur de l'effet, puisque `form` et `dial` sont
     * déstructurés plus bas. Un hook ne peut pas dépendre de ce qui n'est calculé qu'après lui.
     */
    const announcedContact = useRef('');
    useEffect(() => {
        const f = cs?.form;
        if (!f)
            return;
        const liveCfg = context.codConfig ?? DEFAULT_COD_CONFIG;
        const complete = (v) => moroccanPhoneRuleApplies(liveCfg, context.store?.countryCode)
            ? isValidMoroccanPhone(v)
            : phoneDigits(v).length >= 9;
        // Resolved by field TYPE, never by key: the seller's email field has a GENERATED id (`custom-6`), so
        // `f.email` was always empty here and this beacon only ever carried a phone number (16/10/2026).
        const email = buyerEmailOf(liveCfg, f);
        const national = (f.phone ?? '').trim();
        const emailOk = email.includes('@') && email.length > 4;
        const phoneOk = complete(national);
        if (!emailOk && !phoneOk)
            return;
        const phone = phoneOk
            ? `${dialCodeFor(phoneCountryOf(liveCfg, context.store?.countryCode))} ${national}`.trim()
            : '';
        const key = `${emailOk ? email : ''}|${phone}`;
        if (key === announcedContact.current)
            return;
        const timer = window.setTimeout(() => {
            announcedContact.current = key;
            announceContact({
                email: emailOk ? email : null,
                phone: phone || null,
                name: (f.name ?? '').trim() || null,
                locale: context.locale ?? null,
            });
        }, 1200);
        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- les déclencheurs sont les champs saisis
    }, [cs?.form, cs?.form.phone, cs?.form.name, context.store?.countryCode]);
    // Rendered only inside a main-product buy-box; a null checkout state = not in a product buy-box → nothing.
    if (!cs)
        return null;
    const cfg = context.codConfig ?? DEFAULT_COD_CONFIG;
    const bs = block.settings;
    const cls = `aqx-cf-${block.id}`;
    // Scoped class for the quantity-offer tiles rendered at the TOP of the form (feature 0026-07). Deterministic
    // (keyed on the block id, distinct from `cls`) so the tiles' scoped stylesheet is byte-identical SSR↔hydration.
    const offerCls = `aqx-qo-${block.id}`;
    // The block's scoped stylesheet: visibility + the whole cod-form design system. Deterministic (keyed on the
    // class), so SSR and client hydration are byte-identical.
    const css = visibilityCss(cls, bs.visibility) + codFormCss(cls);
    // The page's own direction, not a setting of the form (feature 0050 lot 0). The COD config used to carry a
    // `direction` toggle of its own, which is how an Arabic shop could render a left-to-right order form inside
    // a right-to-left page. There is one answer to "which way does this store read", and the context holds it.
    const dir = context.dir === 'rtl' ? 'rtl' : 'ltr';
    // Input STYLE variant (the keystone design control) → the `data-cf-input` attribute the stylesheet keys on.
    const variant = typeof bs.inputVariant === 'string' &&
        CF_VARIANTS.includes(bs.inputVariant)
        ? bs.inputVariant
        : 'outlined';
    // Label position → `data-cf-labels` (above | floating(Shrine) | hidden). Drives the scoped stylesheet.
    const labelStyle = bs.labelStyle === 'floating' || bs.labelStyle === 'hidden' ? bs.labelStyle : 'above';
    // The delivery-fields section heading (schema `formTitle`, default "Informations de livraison"). Empty = none.
    const formTitle = (typeof bs.formTitle === 'string' ? bs.formTitle : FORM_TITLE).trim();
    // The order button's text: the block's own (schema `buttonLabel`) when set, else the COD app's store-wide one.
    // `pageLabel` is the seller's text for THIS page — hers on every payment method (see `orderLabel` below).
    const pageLabel = typeof bs.buttonLabel === 'string' ? bs.buttonLabel.trim() : '';
    const buttonLabel = pageLabel || cfg.buttonLabel;
    // Style = the dashboard theme (base) → the builder's design overrides → the block's margins. An unset builder
    // field emits nothing, so it falls through to the dashboard theme, then to the stylesheet's literal fallback.
    // `cfVars` (the --cf-* tokens WITHOUT the margins) is re-applied on the portalled popup so it keeps its look.
    const cfVars = { ...resolveThemeVars(cfg.theme), ...builderCfOverrides(bs) };
    const style = { ...cfVars, ...marginStyle(bs, 15, 15) };
    const fields = cfg.fields.filter((f) => f.enabled);
    const { form, setForm } = cs;
    // The form's phone country — the seller's pick in the Form designer first, the store's own country only
    // as the fallback. The dial codes / flags / examples live in `@aquilixshop/cod-config`, so this form and
    // the dashboard's preview of it can never disagree about what the buyer will see (21/10/2026).
    const countryCode = phoneCountryOf(cfg, context.store?.countryCode);
    const dial = dialCodeFor(countryCode);
    const flag = flagEmojiFor(countryCode);
    const submitting = cs.status === 'submitting';
    const isEditor = context.renderMode === 'edit';
    // The methods the STORE accepts — declared by the host on the context (`enabledPaymentMethods`: the
    // storefront from the page bundle, the builder from GET /api/store/payments/account). The seller no longer
    // picks a method on this block: a block setting could contradict what the store can actually honour, and
    // that contradiction is exactly what silently turned a COD-configured page into a card-only one.
    // UNDEFINED means the host never said (older documents, test harnesses) — fall back to COD, the method
    // every store had before card acceptance existed.
    const declaredMethods = context.enabledPaymentMethods ?? DEFAULT_METHODS;
    // In the BUILDER the seller is DESIGNING and there is no payment probe, so a DECLARED card counts as
    // mountable; live, only a card the store can really charge does (founder feedback 2026-07-31 — the canvas
    // showed "Payer par carte" while the live page showed the COD funnel).
    const cardStatus = isEditor ? 'ready' : cs.cardEnabled ? 'ready' : 'unavailable';
    // The SHARED rule (same function the /checkout island applies), so the two surfaces agree about this store.
    // A store accepts ONE method (founder decision 2026-08-01), so this resolves rather than offers a choice —
    // the buyer is never asked. `resolveSelected` still runs it through the same funnel the checkout uses.
    const offered = offerableMethods(declaredMethods, cardStatus);
    const cardMode = resolveSelected(offered, null) === 'CARD';
    // The text painted on the order button — the popup TRIGGER and the CTA inside share it, they are the same
    // button on two layouts. On a CARD store the COD app's store-wide text is written for cash on delivery
    // (« Commander — Paiement à la livraison ») and is NOT a label for a card sale: avernoa's product page
    // said « Paiement à la livraison » outside the popup and « Payer par carte » inside it (founder,
    // 23/09/2026). The card wording wins there, unless the seller typed a text for this page.
    const orderLabel = cardMode ? pageLabel || t(context, 'checkout.pay_by_card') : buttonLabel;
    // Effective order action (feature 0030 — catalog mode). The per-block `orderAction` setting drives the CTA; an
    // explicit value ('cod' | 'add-to-cart' | 'buy-now') always wins and renders in the builder immediately (it's a
    // block setting). For `'auto'` (default): CARD ⇒ the catalog flow — a card buy-box shows a button ("Ajouter au
    // panier") and NEVER the on-page delivery form, because with Stripe the customer fills their details + card on
    // the CHECKOUT page, not the product page (founder direction 2026-07-19). COD ⇒ the on-page order form (the
    // Releasit-style funnel). A store-level cart paradigm (`checkoutStyle:'cart'`) also implies the button. To keep
    // the inline Stripe Payment Element on the product page (the 0026 flow), set `orderAction:'cod'` explicitly.
    const rawAction = typeof bs.orderAction === 'string' ? bs.orderAction : 'auto';
    // APP-PLATFORM GATE (feature 0026): the on-page COD order form is the `cod` app's storefront surface, so it
    // renders ONLY while that app is installed + ACTIVE (`cfg.enabled`, injected by the backend from the install
    // status). The flag rides `codConfig`, so SSR, the hydration island and the builder canvas all agree.
    const codAvailable = cfg.enabled;
    // The SHARED answer — the builder's header lock reads the very same function (see `resolveOrderAction`).
    // Feature 0078 lot B — a DIGITAL product is sold by card, through the checkout (where the e-mail is asked and
    // no address is): never the on-page cash-on-delivery form, whatever the block says.
    const digital = ps?.product.requiresShipping === false;
    const resolvedAction = resolveOrderAction(rawAction, codAvailable, context.checkoutStyle);
    const effectiveAction = digital && resolvedAction === 'cod' ? 'add-to-cart' : resolvedAction;
    const cardRevealed = !!cs.cardIntent;
    const done = cs.status === 'done' && !reopen;
    const btnStyle = {
        ...orderCtaStyle('var(--cf-btn-bg, #1b1b1e)', 'var(--cf-btn-text, #ffffff)', bs.btnUppercase === true),
        borderRadius: 'var(--cf-btn-radius, 12px)',
        minBlockSize: 'var(--cf-btn-min-h, 54px)',
        // The seller's "Button text size / weight" (design.ts) — the inline CTA reads them from `.cf-cta-main`,
        // the popup trigger and the cart button carry this style instead, so all three follow the same setting.
        // Size only when set: an unset size keeps inheriting, exactly as before the control existed.
        fontWeight: 'var(--cf-btn-weight, var(--aqx-theme-fw-bold, 700))',
        ...(typeof bs.btnFontSize === 'number' ? { fontSize: 'var(--cf-btn-font-size)' } : {}),
        inlineSize: 'var(--cf-btn-width, 100%)',
        // The popup trigger measures itself so its label never breaks in two (founder, 22/09/2026). ONLY when
        // the width comes from the column: « Bouton pleine largeur » off sets `--cf-btn-width:fit-content`
        // (design.ts), and inline-size containment would resolve that to nothing — a button with no width at
        // all. A fit-content button grows to its own one-line label anyway, so it never needs the ceiling.
        ...(bs.btnFullWidth === false ? {} : CTA_FIT_CONTAINER),
        cursor: submitting ? 'wait' : 'pointer',
    };
    // ── the upsell chain's clock — HOISTED above the catalog early return ─────────────────────────────
    // Every hook this component calls must run on every render, whatever `orderAction` says: the block is
    // re-rendered in place when the seller switches « Commander » → « Ajouter au panier » in the inspector,
    // and a hook that only the COD path reached made React throw « Rendered fewer hooks than expected » —
    // the buy box then showed « Ce bloc n'a pas pu s'afficher » until the page was reloaded (2026-09-11).
    // Layout (feature 0026): `popup` shows a trigger button that opens the form in a centred modal (the SAME real
    // popup in the builder and on the store — the modal is portalled to document.body so the builder's device-frame
    // `contain:paint` can't trap it); `embedded`/`full` render the form inline.
    // Presentation is a BLOCK setting (feature 0030 · step 4) so it can differ per page. An unset block falls
    // back to the app's stored `layout`, which is what every page carried before the move — so nothing changes
    // presentation on its own, and a seller who had chosen "Popup" in the app keeps it until they choose here.
    const isPopup = resolveLayout(bs.layout, cfg.layout) === 'popup';
    const chainOffers = chain?.offers ?? [];
    const currentOffer = chainOffers[offerIndex];
    /** The chain is on screen only when its host shell is open — the fetch may well have run before that. */
    const chainVisible = isPopup ? popupOpen : chainGateOpen;
    /** Start this offer's countdown the first time it is SHOWN, and never again — the rule itself lives in
     *  `upsell.ts` since 0069 lot C, because the cart journey plays the same chain and must not restart it
     *  by its own arithmetic. */
    const seedDeadlineHere = (offer) => {
        setDeadlines((d) => seedDeadline(d, offer));
    };
    /**
     * The countdown starts when the offer is SEEN — never when it was fetched.
     *
     * In the EMBEDDED layout the chain is prefetched from the buyer's first keystroke, sometimes minutes
     * before they tap "Commander", so seeding at fetch time handed them an offer whose "last minutes" had
     * been running while they typed their address: a 10-minute timer showing 6 minutes on its first frame, or
     * already expired (which hides the banner entirely). Seeding here instead ties the clock to the moment it
     * starts meaning something. Idempotent, so re-renders never restart a timer already running.
     */
    useEffect(() => {
        if (!chainVisible || chainPhase !== 'offer')
            return;
        seedDeadlineHere(currentOffer);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- seedDeadlineHere is re-created every render
    }, [chainVisible, chainPhase, currentOffer]);
    // ── CATALOG mode (feature 0030) ───────────────────────────────────────────────────────────────────────
    // "Ajouter au panier" (add-to-cart → /cart → /checkout) or "Acheter maintenant" (buy-now → straight to
    // /checkout). This buy-box block drops the COD/card funnel entirely and renders just the CTA (variant +
    // quantity come from the sibling variant-picker / quantity blocks via the shared product state). Reuses the
    // block's own scoped stylesheet + design tokens (cls / style / btnStyle) so the button keeps the seller's
    // button design. The heavy COD summary / offer / field computation below is skipped by this early return.
    // Feature 0078 lot B — a file in a store that takes no card cannot be bought at all: say so, in place of a
    // button that would lead to a checkout the server refuses. After every hook (see above), like the return below.
    if (digital && !cardMode) {
        return (_jsx("div", { className: cls, dir: dir, style: style, "data-aqx-buybox": "", children: _jsx("p", { style: DIGITAL_UNAVAILABLE_STYLE, role: "status", "data-testid": "buybox-digital-unavailable", children: t(context, 'product.digital_unavailable') }) }));
    }
    if (effectiveAction === 'add-to-cart' || effectiveAction === 'buy-now') {
        if (!ps)
            return null;
        // Catalog mode = JUST the button (no buy-box card frame). Skip the codFormCss card stylesheet (its
        // border / background / box-shadow / padding wrap the COD form, not a lone button); keep only the
        // visibility rule + the inline `--cf-*` tokens (for the button colors) + the block margins.
        const bareCss = visibilityCss(cls, bs.visibility);
        // BUILDER-ONLY explainer: without it, a seller whose COD app is uninstalled opens the builder, finds
        // "Ajouter au panier" where their order form used to be and has no way to know why. Never rendered live.
        const codOffNotice = isEditor && !codAvailable;
        return (_jsxs(_Fragment, { children: [bareCss ? _jsx("style", { dangerouslySetInnerHTML: { __html: bareCss } }) : null, _jsxs("div", { className: cls, dir: dir, style: style, "data-aqx-buybox": "", children: [codOffNotice ? (_jsx("p", { style: COD_OFF_NOTICE_STYLE, "data-testid": "cod-app-off-notice", children: t(context, 'builder.cod_app_off') })) : null, digital ? (_jsx("p", { style: DIGITAL_NOTE_STYLE, "data-testid": "buybox-digital-note", children: t(context, 'product.digital_note') })) : null, hasOfferTiles(context, ps.product.id) ? (
                        // The offer is a CARD here (28/09/2026), and the button closes it — Pumper, WideBundle and
                        // Bundle Bear all put «Add to cart» inside the quantity-break card, under a «Total» line. The
                        // gap below is gone with it: the card's own padding is the breathing room now.
                        _jsx(OfferTiles, { context: context, className: offerCls, style: OFFER_TILE_VARS, framed: true, footer: _jsx(CartAddBox, { context: context, ps: ps, btnStyle: btnStyle, action: effectiveAction, pageLabel: pageLabel }) })) : (_jsx(CartAddBox, { context: context, ps: ps, btnStyle: btnStyle, action: effectiveAction, pageLabel: pageLabel }))] })] }));
    }
    // ── summary pricing (from the SELECTED offer tier's pack price; COD default = free shipping) ──────────────
    // Bound to the SAME server-authoritative offer data the tiles use: the current variant's unit price × the
    // discount rule for the tier whose pack size matches the shared quantity. No offer / no tier → base price × qty.
    const offer = context.activeOffer;
    const qty = ps?.quantity ?? 1;
    // The priced variants are the PRODUCT's variants (they carry `.price`), matched by the shared `variantId` —
    // the same source `OfferTiles` prices from, so the summary total tracks the tiles.
    const pricedVariants = ps?.product.variants ?? [];
    const activeVariant = pricedVariants.find((v) => v.id === ps?.variantId) ?? pricedVariants[0];
    // Fall back to the product's base price when there is no priced variant (builder/demo product carries a base
    // `price` but no `variants` array) — otherwise the summary would render a 0,00 total on the authoring surface.
    const unitCents = activeVariant?.price.amount ?? ps?.product.price.amount ?? 0;
    const currency = activeVariant?.price.currency ?? ps?.product.price.currency ?? offer?.currency ?? 'MAD';
    // Match the backend engine (QuantityOfferEngine.selectTier): the HIGHEST tier whose pack size is <= the cart
    // quantity — NOT an exact-qty match. So an off-tier quantity still resolves to the tier checkout charges, and
    // the summary total equals what the buyer is billed (base tier qty=1 always qualifies).
    const tier = offer && ps && offer.productId === ps.product.id
        ? offer.tiers.reduce((best, t) => (t.qty <= qty && (!best || t.qty > best.qty) ? t : best), undefined)
        : undefined;
    // A variant PER UNIT (28/09/2026): the pack is the sum of its units' own list prices, each discounted by
    // the tier — the checkout engine's arithmetic for a mixed basket. With every unit on the picked variant
    // this is exactly `unitCents × qty`, as before.
    const unitIds = ps?.unitVariantIds ?? [];
    const unitPrices = unitIds.length === qty
        ? unitIds.map((id) => pricedVariants.find((v) => v.id === id)?.price.amount ?? unitCents)
        : Array.from({ length: qty }, () => unitCents);
    const currencyMatches = !!offer && currency.toUpperCase() === offer.currency.toUpperCase();
    const packBase = unitPrices.reduce((sum, c) => sum + c, 0);
    const packPrice = tier && offer
        ? unitPrices.reduce((sum, c) => sum + tierUnitPrice(c, tier, currencyMatches), 0)
        : packBase;
    const save = Math.max(0, packBase - packPrice);
    // What the accepted upsell offers add (feature 0042 lot D). DISPLAY only, like every other number in this
    // summary: the server re-prices from `(offerId, variantId, quantity)` and its answer is the one that counts.
    const upsellTotalCents = acceptedTotalCents(accepted);
    // Applied promo (feature 0014): display-only like every number here — capped at the payable subtotal so
    // a generous fixed-amount code can never paint a negative total. The server's re-price is the authority.
    const promoCents = cs.promo ? Math.min(cs.promo.amountCents, packPrice + upsellTotalCents) : 0;
    const totalLabel = formatOfferMoney(packPrice + upsellTotalCents - promoCents, currency, context.locale); // free shipping → total == subtotal
    const subTotalLabel = formatOfferMoney(packPrice, currency, context.locale);
    const saveLabel = save > 0 ? `−${formatOfferMoney(save, currency, context.locale)}` : null;
    const qtyUnitLabel = t(context, 'cod.unit_count', { count: qty });
    // The single phone predicate for this render: the Moroccan shape rule when the seller left that validation on,
    // otherwise a plain "looks long enough". Feeds the green tick, the field error AND the submit gate, so the
    // three can never say different things about the same number.
    const phoneComplete = (v) => moroccanPhoneRuleApplies(cfg, context.store?.countryCode)
        ? isValidMoroccanPhone(v)
        : phoneDigits(v).length >= 9;
    // Per-field error (display-only; `cs.placeOrder` re-validates server-side). Required-empty → "Champ requis";
    // a phone that is not one of the accepted Moroccan shapes → the "9 chiffres" message when that validation is
    // on. It fires on a number that is too LONG as well as too short: an over-long entry is the double-prefix
    // typo, and letting it through costs a real WhatsApp send to a number that cannot exist.
    const fieldError = (f) => {
        if (!attempted)
            return null;
        const v = (form[f.id] ?? '').trim();
        if (f.required && !v)
            return t(context, 'form.required');
        if (f.type === 'phone' && v && !phoneComplete(v)) {
            return t(context, 'error.invalid_phone');
        }
        return null;
    };
    // True when an enabled phone field holds a number the rule rejects — the same predicate fieldError() paints,
    // but independent of `attempted` so submit() can consult it synchronously.
    const hasInvalidPhone = () => fields.some((f) => {
        const v = (form[f.id] ?? '').trim();
        return f.type === 'phone' && v !== '' && !phoneComplete(v);
    });
    // NOTE: a `hasMissingRequired()` guard used to live here, because the EMBEDDED layout played the offer
    // chain BETWEEN the tap and the order — a buyer on a half-filled form was shown an offer, answered it,
    // and only then learnt their order never left. That gate is gone with the embedded chain itself. If
    // anything is ever put between "Commander" and `placeOrder` again, bring the guard back with it.
    // The CTA action — shows the per-field errors AND places the order via the SHARED checkout state (never a
    // fake timeout). An invalid phone (the COD fulfillment channel) must BLOCK the order, not just redden the
    // field; the backend re-validates but its phone guard is length-agnostic. `reopen` is cleared so a re-submit
    // after "Modifier" re-shows the success card.
    const submit = () => {
        setAttempted(true);
        if (hasInvalidPhone())
            return;
        setReopen(false);
        // Tells the storefront's measurement beacon that a shopper committed their details. This one-page funnel
        // has no cart and no checkout page, so without this announcement the merchant's Live view funnel would
        // read 0 carts, 0 checkouts and N orders for a shop that sells perfectly well — and they would rightly
        // conclude the tool is broken. Never fired inside the builder: that is the seller, not a customer.
        if (!isEditor)
            announceCheckoutIntent();
        // No pre-purchase gate here any more: the chain belongs to the POPUP layout, and there it has already
        // played at the popup's opening (founder, 2026-08-12). An embedded form places the order on the tap,
        // which is what it always looked like it did.
        runSubmit();
    };
    /** Place the order for real. Reached directly, or once the offer chain has been answered. */
    const runSubmit = () => {
        // From here the accepted upsell lines are frozen (see `submitSent`): the body this key names is the one
        // any retry replays, so the summary must stop being editable the moment it leaves.
        setSubmitSent(true);
        void cs.placeOrder({
            method: cardMode ? 'CARD' : 'COD',
            upsell: upsellSubmission(chain, accepted),
            // The POST-purchase offer plays HERE, in the buy-box the buyer is already looking at, and the jump to
            // the thank-you page waits for it (founder, 2026-08-12). The order is already placed and confirmed
            // server-side; only the navigation is held. `proceed` is stored, not called, and the chain component
            // owes us exactly one call to it — every one of its exits (answered, skipped, no chain at all,
            // backend unreachable) leads there, or the buyer would be stranded on a form for an order that is
            // already theirs.
            onPlaced: postPurchaseEligible
                ? (orderId, proceed) => {
                    proceedRef.current = proceed;
                    setPostOrderId(orderId);
                }
                : undefined,
        });
    };
    // Re-pointed every render so the deferred submit closes over the CURRENT accepted list.
    submitAfterChain.current = runSubmit;
    const ctaMain = submitting ? t(context, 'checkout.confirming') : orderLabel;
    // `ltrIsolate` — the amount is spliced INTO a sentence here, so there is no element to style: on an Arabic
    // shop the sub-line painted « الدفع عند الاستلام · dh 899,00 ». The two invisible isolates keep the amount
    // left-to-right without touching the direction of the words around it (see `_shared/ltrIsolate.ts`).
    const ctaSub = cardMode
        ? t(context, 'checkout.pay_secure_with_total', { total: ltrIsolate(totalLabel) })
        : t(context, 'checkout.pay_on_delivery_with_total', { total: ltrIsolate(totalLabel) });
    // Sticky mobile bar shows the total separately, so its CTA sub-line is the method WITHOUT the amount (and must
    // still track card vs COD, unlike the previously hardcoded "Paiement à la livraison").
    const ctaStickySub = cardMode
        ? t(context, 'checkout.pay_secure')
        : t(context, 'checkout.pay_on_delivery');
    const successPhone = `${dial} ${form.phone ?? ''}`.trim();
    // ONE LINE (founder, 22/09/2026: « supprime le retour en ligne on doit pas avoir ça », screenshot of
    // « JE COMMANDE MON MOVE Shift - 55% » broken across two lines). The button is `container-type:
    // inline-size` (design.ts), so the line asks it how much room it has and caps itself instead of wrapping.
    //
    // ONLY while the size is « Défaut » — `btnFontSize` unset. A number the seller typed is painted as she
    // typed it: « aucun réglage de texte ne plafonne » (founder, 20/09/2026), and a slider she drags to 60
    // that paints 31 reads as a broken control, not as a clever fit. Her number wins, and at her number the
    // text is allowed to wrap again.
    const sellerSetSize = typeof bs.btnFontSize === 'number';
    // Geometry: the CTA has no inline padding of its own, so 20px covers the rounding + the sticky bar's 14px.
    const CTA_BASE = 'var(--cf-cta-fs, var(--cf-btn-font-size, 15px))';
    const ctaMainFit = sellerSetSize
        ? undefined
        : oneLineCta(ctaMain, CTA_BASE, { padPx: 20, trackEm: -0.01 });
    // The sub-line has no size of its own to respect, and `.cf-cta` is a fixed 54 px high: a wrapped
    // sub-line simply spills out of the button.
    const ctaSubFit = oneLineCta(stripLtrIsolate(ctaSub), '12px', { padPx: 20 });
    const ctaStickySubFit = oneLineCta(ctaStickySub, '12px', { padPx: 20 });
    const triggerFit = sellerSetSize
        ? undefined
        : oneLineCta(orderLabel, '1em', { padPx: 60, trackEm: bs.btnUppercase === true ? 0.05 : 0 });
    const cta = (_jsxs("button", { type: "button", className: "cf-cta", onClick: submit, disabled: submitting, "aria-busy": submitting || undefined, children: [_jsx("span", { className: "cf-cta-main", style: ctaMainFit, children: ctaMain }), _jsx("span", { className: "cf-cta-sub", style: ctaSubFit, children: ctaSub })] }));
    const live = context.renderMode !== 'edit';
    // Promo-code field: seller opt-in per page (schema `promoField`, default off). Strict === true so the
    // absent value on every existing page reads as OFF.
    const promoFieldOn = bs.promoField === true;
    // Re-pointed every render (like `submitAfterChain`) so a promo quote always prices the CURRENT cart.
    promoQuoteCtx.current = {
        variantId: ps?.variantId ?? '',
        quantity: qty,
        lines: ps ? basketLines(ps) : undefined,
        card: cardMode,
        upsell: upsellSubmission(chain, accepted),
    };
    /** Apply the typed code: preview-quote it server-side, then pin it on the shared checkout state. */
    const applyPromo = () => {
        const code = promoInput.trim();
        if (!code || promoBusy)
            return;
        if (!live || cs.isDemo) {
            // Builder / demo: the ids are synthetic and the preview would 404 — say so instead of "invalid".
            setPromoError(t(context, 'cod.promo_online_only'));
            return;
        }
        setPromoBusy(true);
        setPromoError(null);
        void quotePromo(code, promoQuoteCtx.current).then((res) => {
            setPromoBusy(false);
            if (res.kind === 'ok') {
                cs.setPromo({ code, amountCents: res.amountCents, currency: res.currency });
                setPromoInput('');
                setPromoError(null);
            }
            else if (res.kind === 'refused') {
                setPromoError(t(context, res.messageKey));
            }
            else {
                setPromoError(t(context, 'cod.promo_check_failed'));
            }
        });
    };
    // ── the pre-purchase upsell chain (feature 0042 lot D) ────────────────────────────────────────────────
    // POPUP LAYOUT ONLY (founder, 2026-08-12). The popup is a threshold the buyer chose to cross, and the
    // offer belongs on it. An inline form has no threshold — its only moment is the "Commander" tap, and an
    // offer there is indistinguishable from the post-purchase one, which is the distinction the two modes
    // exist to carry. So an embedded form simply places the order, with no pre-purchase offer at all.
    // Never in the builder or the demo either: those ids are synthetic, and a chain fetched for them would
    // ask the server about a product that does not exist.
    const upsellEligible = live && !cs.isDemo && isPopup && !!ps?.product.id;
    /**
     * The POST-purchase chain plays in this buy-box too, right after the order is placed and BEFORE the
     * thank-you page (founder, 2026-08-12) — it used to overlay the confirmation, one screen too late, when
     * the buyer had already mentally left. Same liveness guard as the pre-purchase chain: never in the
     * builder, never in the demo, where the order id is synthetic.
     */
    const postPurchaseEligible = live && !cs.isDemo;
    /**
     * The chain is over — answered, abandoned, or never there. In the popup the form takes its place; in the
     * embedded form the modal closes and the order the buyer was placing goes out, so finishing the chain
     * never costs them a second click on "Commander".
     */
    const finishChain = () => {
        setChainPhase('done');
        // Closing the shell is what releases a pending order — the effect above places it once the state has
        // settled. Dismissing the offers therefore never cancels the purchase the buyer already asked for.
        setChainGateOpen(false);
    };
    /** Move to the next offer, or hand over when the chain is exhausted. */
    const advanceChain = () => {
        const next = offerIndex + 1;
        if (next >= chainOffers.length) {
            finishChain();
            return;
        }
        // The next offer's countdown is seeded by the effect above, when it reaches the screen.
        setOfferIndex(next);
    };
    /**
     * Load the chain once per mount. Returns the offers it found, so the caller can decide whether there is
     * anything to show — the embedded gate needs that answer synchronously enough to place the order when
     * there is nothing.
     */
    const loadChain = async () => {
        if (!ps)
            return [];
        setChainPhase('loading');
        const fetched = await fetchUpsellChain(ps.product.id, { store: devStoreParam() });
        if (!fetched) {
            setChainPhase('done');
            return [];
        }
        setChain(fetched);
        setOfferIndex(0);
        // No seeding here: the fetch may be minutes ahead of the moment the buyer actually sees this offer.
        setChainPhase('offer');
        return fetched.offers;
    };
    /**
     * Warm the chain on the buyer's INTENTION, before the shell is handed over: pointing at / tabbing to /
     * pressing the popup trigger, and the first keystroke in a form that has one.
     *
     * It used to refuse the popup layout outright (`if (isPopup || …)`) while [upsellEligible] REQUIRED the
     * popup — the two conditions could never both hold, so this never ran at all, and the popup always opened
     * on a skeleton and asked the server afterwards. On a product with no chain that cost every buyer ~740 ms
     * of offer-shaped grey bars before the order form (measured 23/09/2026). One guard, one meaning: warm when
     * there is a chain worth warming and nobody has asked yet.
     */
    const prefetchChain = () => {
        if (chainPhase !== 'idle' || !upsellEligible || !ps)
            return;
        void loadChain();
    };
    /**
     * The trigger, the ✕, the scrim and Escape all land here.
     *
     * Opening runs the ONE fetch of the mount, and the shell opens IMMEDIATELY in its loading state — a buyer
     * who tapped the trigger must never face three seconds of a dead button. Every failure (no chain, an
     * error, a timeout) lands on `done`, which is the order form: the shop keeps the sale it already had.
     *
     * Closing mid-chain ABANDONS the chain. Someone who dismissed the offer has answered it; re-imposing it
     * when they reopen the form would be the behaviour that makes shoppers hate popups.
     */
    const onPopupOpenChange = (next) => {
        setPopupOpen(next);
        if (!next) {
            if (chainPhase === 'loading' || chainPhase === 'offer')
                setChainPhase('done');
            return;
        }
        if (chainPhase !== 'idle' || !upsellEligible || !ps)
            return;
        void loadChain();
    };
    const onAcceptOffer = (line) => {
        setAccepted((a) => withAcceptance(a, line));
        advanceChain();
    };
    /**
     * What the chain renders right now, or null when there is nothing of its own to show (then the host
     * renders the order form). `loading` is a skeleton rather than a spinner over nothing, so the shell the
     * buyer asked for is on screen instantly.
     */
    const chainUi = !chainVisible ? null : chainPhase === 'loading' ? (_jsxs("div", { className: "cf-ups-load", "aria-busy": "true", children: [_jsx("span", {}), _jsx("span", {}), _jsx("span", {})] })) : chainPhase === 'offer' && currentOffer ? (_jsx(UpsellOfferCard, { offer: currentOffer, index: offerIndex + 1, total: chainOffers.length, deadline: deadlines[currentOffer.offerId] ?? null, onAccept: onAcceptOffer, onReject: advanceChain, onSkipChain: finishChain }, currentOffer.offerId)) : null;
    /**
     * The POST-purchase chain, once the order exists. It takes the form's place in whatever shell the form
     * lives in — the popup, or the card on the page — because that is where the buyer's eyes already are; the
     * thank-you page comes after, carrying whatever they accepted.
     *
     * `onOrderUpdated` is deliberately a no-op: nothing here shows a total. The confirmation page loads the
     * order fresh from the server a moment later, so the number the buyer reads is the server's, never one
     * this component patched together.
     */
    const postChainUi = postOrderId ? (_jsx(PostPurchaseUpsellChain, { orderId: postOrderId, store: devStoreParam(), 
        // The post-purchase offer is ALWAYS a popup to the buyer (founder, 2026-08-12). In the popup layout
        // it takes the place of the form inside the shell that is already open — a modal over a modal would
        // be two things to dismiss for one decision. In the embedded layout there is no shell, so it opens
        // its own overlay rather than growing out of the card, which he did not want.
        presentation: isPopup ? 'inline' : 'modal', onOrderUpdated: () => undefined, onHasContent: setPostChainOnScreen, onFinished: () => proceedRef.current?.() })) : null;
    // The form body — offer tiles, the delivery-info heading, the fields, the summary, and the self-contained
    // order action (selector + CTA / success + card element + trust). Shared by the inline AND popup renders.
    const body = (_jsxs(_Fragment, { children: [_jsx(OfferTiles, { context: context, className: offerCls }), _jsxs("div", { className: "cf-fields-head", children: [formTitle ? _jsx("h3", { className: "cf-heading", children: formTitle }) : null, _jsx("p", { className: "cf-subhead", children: cardMode ? t(context, 'checkout.card_note') : t(context, 'checkout.cod_note') })] }), _jsx("div", { className: "cf-grid", children: fields.map((f) => {
                    const err = fieldError(f);
                    const id = `cod-${block.id}-${f.id}`;
                    const onChange = (v) => {
                        // The first keystroke is the buyer starting to buy — warm the offer chain now. A no-op in the
                        // EMBEDDED layout, which has no pre-purchase chain by design (founder, 2026-08-12): there the
                        // gate is `upsellEligible`, not this call. The popup warms from its trigger instead, where the
                        // buyer's intention actually lands.
                        prefetchChain();
                        setForm((prev) => ({ ...prev, [f.id]: v }));
                    };
                    return (_jsxs("div", { className: "cf-field", "data-err": err ? '' : undefined, children: [_jsxs("label", { htmlFor: id, className: "cf-label", children: [f.label, f.required ? ' *' : ''] }), _jsx("div", { className: "cf-control", children: f.type === 'phone' ? (
                                // Phone → the +dial (country-derived) prefix box + the valid-check.
                                _jsx(PhoneField, { id: id, name: f.id, value: form[f.id] ?? '', placeholder: f.placeholder || phoneNationalExampleFor(countryCode) || f.label, required: f.required, ariaLabel: f.label, dial: dial, flag: flag, isComplete: phoneComplete, onChange: onChange })) : (renderField(f, block.id, form[f.id] ?? '', countryCode, onChange)) }), err ? _jsx("div", { className: "cf-err", children: err }) : null] }, f.id));
                }) }), !promoFieldOn || cs.promo ? null : (_jsxs("div", { className: "cf-promo", children: [promoOpen ? (_jsxs("div", { className: "cf-promo-row", children: [_jsx("input", { className: "cf-input", type: "text", value: promoInput, placeholder: t(context, 'cod.promo_placeholder'), "aria-label": t(context, 'cod.promo_placeholder'), onChange: (e) => {
                                    setPromoInput(e.currentTarget.value);
                                    setPromoError(null);
                                }, onKeyDown: (e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        applyPromo();
                                    }
                                } }), _jsx("button", { type: "button", className: "cf-promo-btn", onClick: applyPromo, disabled: promoBusy || !promoInput.trim(), children: promoBusy ? t(context, 'cod.promo_checking') : t(context, 'cod.promo_apply') })] })) : (_jsx("button", { type: "button", className: "cf-promo-toggle", onClick: () => setPromoOpen(true), children: t(context, 'cod.promo_toggle') })), promoError ? _jsx("p", { className: "cf-promo-err", children: promoError }) : null] })), _jsxs("div", { className: "cf-summary", children: [_jsxs("div", { className: "cf-sum-row", children: [_jsx("span", { className: "cf-sum-label", children: t(context, 'cart.subtotal_with_units', { units: qtyUnitLabel }) }), _jsx("span", { className: "cf-sum-val", children: subTotalLabel })] }), accepted.map((line) => (_jsxs("div", { className: "cf-sum-row cf-sum-ups", children: [_jsxs("span", { className: "cf-sum-label", children: [line.title, line.variantLabel && line.variantLabel !== line.title
                                        ? ` — ${line.variantLabel}`
                                        : '', line.quantity > 1 ? ` × ${line.quantity}` : ''] }), _jsxs("span", { className: "cf-sum-val", children: [formatOfferMoney(line.unitCents * line.quantity, line.currency, context.locale), submitSent ? null : (_jsx("button", { type: "button", className: "cf-sum-x", onClick: () => setAccepted((a) => withoutAcceptance(a, line.offerId)), "aria-label": t(context, 'cart.remove_named', { name: line.title }), children: "\u00D7" }))] })] }, line.offerId))), _jsxs("div", { className: "cf-sum-row", children: [_jsx("span", { className: "cf-sum-label", children: t(context, 'cart.shipping') }), _jsx("span", { className: "cf-sum-free", children: t(context, 'cart.shipping_free') })] }), saveLabel ? (_jsxs("div", { className: "cf-sum-save", children: [_jsx("span", { children: t(context, 'cart.you_save') }), _jsx("span", { children: saveLabel })] })) : null, cs.promo ? (_jsxs("div", { className: "cf-sum-row cf-sum-promo", children: [_jsxs("span", { className: "cf-sum-label", children: [t(context, 'cart.promo_code_label', { code: cs.promo.code }), submitSent ? null : (_jsx("button", { type: "button", className: "cf-sum-x", onClick: () => {
                                            cs.setPromo(null);
                                            // And out of the jar, or the promoted code would come straight back on the next
                                            // render — the auto-apply effect above reads it there, not from this state.
                                            clearPromoCode();
                                        }, "aria-label": t(context, 'cart.remove_promo'), children: "\u00D7" }))] }), _jsxs("span", { className: "cf-sum-val", children: ["\u2212", formatOfferMoney(promoCents, currency, context.locale)] })] })) : null, _jsx("div", { className: "cf-sum-divider" }), _jsxs("div", { className: "cf-sum-total", children: [_jsx("span", { className: "cf-sum-total-label", children: t(context, 'cart.total') }), _jsx("span", { className: "cf-sum-total-val", children: totalLabel })] })] }), _jsxs("div", { className: "cf-actions", children: [done ? (
                    // Success ("Commande reçue") card — the buyer will be called on the number they gave to confirm.
                    _jsxs("div", { className: "cf-done", children: [_jsxs("svg", { width: "22", height: "22", viewBox: "0 0 22 22", children: [_jsx("circle", { cx: "11", cy: "11", r: "11", fill: "var(--cf-accent,#0e7a5f)" }), _jsx("path", { d: "M6.5 11.3 L9.6 14.4 L15.5 7.8", fill: "none", stroke: "#ffffff", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })] }), _jsxs("div", { children: [_jsx("div", { className: "cf-done-title", children: t(context, 'checkout.order_received') }), _jsxs("div", { className: "cf-done-text", children: [t(context, 'checkout.we_will_call_before'), _jsx("span", { className: "cf-done-phone", children: successPhone }), t(context, 'checkout.we_will_call_after')] }), _jsx("button", { type: "button", className: "cf-done-edit", onClick: () => setReopen(true), children: t(context, 'checkout.edit_order') })] })] })) : !cardRevealed ? (
                    // Primary submit — the two-line dark CTA; hidden once the card element is revealed.
                    cta) : null, cs.message && cs.status === 'error' ? (_jsx("p", { role: "status", className: "cf-msg-err", children: cs.message })) : null, cardMode && isEditor && !cardRevealed ? (_jsxs("div", { "aria-hidden": "true", style: {
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                            border: '1px solid var(--aqx-scheme-border, #e0dce8)',
                            borderRadius: 'var(--cf-btn-radius, 12px)',
                            padding: '13px 14px',
                            marginTop: 'var(--aqx-theme-space-3, 12px)',
                            background: 'var(--aqx-scheme-bg, #ffffff)',
                            color: 'var(--aqx-scheme-muted, #8a8595)',
                            fontSize: 'var(--aqx-theme-text-sm, 14px)',
                        }, children: [_jsx("span", { style: { fontSize: '11px', fontWeight: 600, letterSpacing: '.02em' }, children: t(context, 'checkout.card_label') }), _jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', gap: '8px' }, children: [_jsx("span", { children: "1234\u00A01234\u00A01234\u00A01234" }), _jsx("span", { style: { fontSize: '10px', fontWeight: 700, letterSpacing: '.04em' }, children: "VISA" })] }), _jsxs("div", { style: { display: 'flex', gap: '22px' }, children: [_jsx("span", { children: "MM\u00A0/\u00A0AA" }), _jsx("span", { children: "CVC" })] })] })) : null, _jsx(CardReveal, { cs: cs, context: context })] })] }));
    return (_jsxs(_Fragment, { children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css } }), _jsxs("div", { className: cls, dir: dir, "data-cf-input": variant, "data-cf-labels": labelStyle, "data-aqx-buybox": "", style: style, children: [_jsx(CodCartBeacon, { active: !!ps && !isEditor && !cs.isDemo, variantId: ps?.variantId ?? '', quantity: qty, 
                        // A variant per unit (28/09/2026): the grouped lines replace the single main line.
                        lines: ps ? basketLines(ps).map((l) => ({ variantId: l.variantId, qty: l.quantity })) : [], formTouched: Object.values(form).some((v) => typeof v === 'string' && v.trim() !== ''), 
                        // Accepted upsell offers are part of the basket the shopper is holding (feature 0042 lot D).
                        extraLines: accepted.map((a) => ({ variantId: a.variantId, qty: a.quantity })) }), isPopup ? (
                    // Popup: a trigger button that opens the form in a centred modal — the SAME real popup in the builder
                    // and on the store (in the builder it opens on mousedown to get past the editor's click-to-select, and
                    // the modal is portalled out of the device-frame so it's actually visible).
                    _jsxs(CodPopup, { triggerLabel: orderLabel, triggerFit: triggerFit, triggerStyle: btnStyle, dir: dir, editor: !live, wrapperClass: cls, wrapperStyle: cfVars, variant: variant, labels: labelStyle, open: popupOpen, onOpenChange: onPopupOpenChange, onWarm: prefetchChain, children: [postChainUi, postChainOnScreen ? null : (chainUi ?? body)] })) : (_jsxs(_Fragment, { children: [body, postChainUi, !done && !cardRevealed && !chainUi && !postChainOnScreen ? (_jsxs("div", { className: "cf-sticky", children: [_jsxs("div", { className: "cf-sticky-total", children: [_jsx("div", { className: "cf-sticky-label", children: t(context, 'cart.total') }), _jsx("div", { className: "cf-sticky-val", children: totalLabel }, totalLabel)] }), _jsxs("button", { type: "button", className: "cf-cta", onClick: submit, disabled: submitting, "aria-busy": submitting || undefined, children: [_jsx("span", { className: "cf-cta-main", style: ctaMainFit, children: ctaMain }), _jsx("span", { className: "cf-cta-sub", style: ctaStickySubFit, children: ctaStickySub })] })] })) : null] }))] })] }));
};
export const codFormBlock = {
    type: 'cod-form',
    schema: codFormBlockSchema,
    Component: CodFormBlock,
    meta: { label: "Boîte d'achat (COD / carte)" },
};
//# sourceMappingURL=index.js.map